import { api } from "./axios";
import { ComplaintStatus, Priority } from "@/components/shared/StatusBadge";

interface Envelope<T> {
  success: true;
  data: T;
  meta?: { page: number; limit: number; total: number };
}

export interface ComplaintListItem {
  id: string;
  complaintNumber: string;
  productName: string;
  status: ComplaintStatus;
  priority: Priority;
  createdAt: string;
  expectedCompletionDate: string | null;
  customer: { id: string; fullName: string; phone: string; customerCode: string };
  assignedEmployee: { id: string; fullName: string } | null;
}

export interface Attachment {
  id: string;
  fileName: string;
  filePath: string;
  fileType: "PHOTO" | "DOCUMENT" | "OTHER";
  fileSize: number;
  createdAt: string;
}

export interface HistoryEvent {
  id: string;
  eventType: string;
  previousStatus: ComplaintStatus | null;
  newStatus: ComplaintStatus | null;
  remarks: string | null;
  workingHours: number | null;
  createdAt: string;
  employee: { id: string; fullName: string } | null;
}

export interface ComplaintDetail extends ComplaintListItem {
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  description: string;
  internalNotes: string | null;
  completedAt: string | null;
  deliveredAt: string | null;
  attachments: Attachment[];
  histories: HistoryEvent[];
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  limit: number;
  total: number;
}

export interface ComplaintFilters {
  page: number;
  limit: number;
  search?: string;
  status?: ComplaintStatus;
  priority?: Priority;
  employeeId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface CreateComplaintInput {
  customerId: string;
  productName: string;
  brand?: string;
  model?: string;
  serialNumber?: string;
  description: string;
  priority: Priority;
  assignedEmployeeId?: string;
  expectedCompletionDate?: string;
  internalNotes?: string;
}

export type UpdateComplaintInput = Partial<Omit<CreateComplaintInput, "customerId">>;

function toPaged<T>(res: { data: Envelope<T[]> }): PagedResult<T> {
  return {
    items: res.data.data,
    page: res.data.meta!.page,
    limit: res.data.meta!.limit,
    total: res.data.meta!.total,
  };
}

export const complaintApi = {
  async list(params: ComplaintFilters): Promise<PagedResult<ComplaintListItem>> {
    const res = await api.get<Envelope<ComplaintListItem[]>>("/complaints", { params });
    return toPaged(res);
  },

  async getById(id: string): Promise<ComplaintDetail> {
    const res = await api.get<Envelope<ComplaintDetail>>(`/complaints/${id}`);
    return res.data.data;
  },

  async create(input: CreateComplaintInput): Promise<ComplaintDetail> {
    const res = await api.post<Envelope<ComplaintDetail>>("/complaints", input);
    return res.data.data;
  },

  async update(id: string, input: UpdateComplaintInput): Promise<ComplaintDetail> {
    const res = await api.put<Envelope<ComplaintDetail>>(`/complaints/${id}`, input);
    return res.data.data;
  },

  async changeStatus(id: string, status: ComplaintStatus, remarks?: string): Promise<ComplaintDetail> {
    const res = await api.patch<Envelope<ComplaintDetail>>(`/complaints/${id}/status`, { status, remarks });
    return res.data.data;
  },

  async assign(id: string, employeeId: string, remarks?: string): Promise<ComplaintDetail> {
    const res = await api.patch<Envelope<ComplaintDetail>>(`/complaints/${id}/assign`, {
      employeeId,
      remarks,
    });
    return res.data.data;
  },

  async addNote(id: string, remarks: string): Promise<ComplaintDetail> {
    const res = await api.post<Envelope<ComplaintDetail>>(`/complaints/${id}/notes`, { remarks });
    return res.data.data;
  },

  async logHours(id: string, workingHours: number, remarks?: string): Promise<ComplaintDetail> {
    const res = await api.post<Envelope<ComplaintDetail>>(`/complaints/${id}/hours`, {
      workingHours,
      remarks,
    });
    return res.data.data;
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/complaints/${id}`);
  },

  async uploadAttachment(id: string, file: File): Promise<Attachment> {
    const formData = new FormData();
    formData.append("file", file);
    const res = await api.post<Envelope<Attachment>>(`/complaints/${id}/attachments`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data.data;
  },

  async removeAttachment(id: string, attachmentId: string): Promise<void> {
    await api.delete(`/complaints/${id}/attachments/${attachmentId}`);
  },
};
